import { Component, computed, effect, inject, input, output, signal, untracked } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { MonitoringOptionCardWithQuantityComponent } from "@project/monitoring/monitoring-option-card-with-quantity/monitoring-option-card-with-quantity.component";
import { OptionDisplay, OptionDisplayService } from "@project/monitoring/option-display.service";
import { forkJoin, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { getConfigurationOptionIdentifier } from "../../../monitoring/monitoring-configuration.utils";

export interface OptionSection {
  header: string;
  options: OptionDisplay[];
}

@Component({
  selector: "app-central-station-option-selector",
  imports: [FilterProductComponent, MonitoringOptionCardWithQuantityComponent, UiKitModule],
  templateUrl: "./central-station-option-selector.component.html",
  styleUrl: "./central-station-option-selector.component.scss",
})
export class CentralStationOptionSelectorComponent {
  private readonly monitoringProductService = inject(MonitoringProductService);
  private readonly optionDisplayService = inject(OptionDisplayService);

  readonly title = input.required<string>();
  readonly category = input.required<MonitoringCategory>();
  readonly categoryLabel = input<string | undefined>(undefined);
  readonly familyName = input<string | undefined>(undefined);
  readonly binaryHeader = input<string | null>(null);
  readonly quantityHeader = input<string | null>(null);
  readonly showCategoryHeaders = input(true);
  readonly options = input<FamilyStepOption[]>([]);
  readonly preselectedOptions = input<MonitoringProductItem[]>();

  readonly selectionChange = output<MonitoringProductItem[]>();

  protected readonly searchValue = signal<string | null>(null);
  private readonly selectedQuantities = signal(new Map<string, number>());
  private preselectedApplied = false;

  private readonly defaultBinaryHeader = $localize`:@@centralStationConfiguration.additionalLicences.singleHeader:Einzellizenzen`;
  private readonly defaultQuantityHeader = $localize`:@@centralStationConfiguration.additionalLicences.quantityHeader:Mengenlizenzen`;

  private readonly optionDisplays = toSignal(
    toObservable(this.options).pipe(
      switchMap((options) => {
        if (!options?.length) return of([] as OptionDisplay[]);
        const category = this.category();
        return forkJoin(options.map((option) => this.optionDisplayService.createOptionDisplay(option, category)));
      }),
    ),
    { initialValue: [] as OptionDisplay[] },
  );

  private readonly filteredOptionDisplays = computed(() => {
    const searchValue = this.searchValue();
    const displays = this.optionDisplays();
    if (!searchValue) return displays;
    return displays.filter((option) => this.monitoringProductService.searchFilterOption(option, searchValue));
  });

  protected readonly sections = computed<OptionSection[]>(() => {
    const filtered = this.filteredOptionDisplays();
    if (!this.showCategoryHeaders()) {
      return [{ header: "", options: this.sortOptions(filtered) }];
    }

    const binaryHeader = this.binaryHeader() ?? this.defaultBinaryHeader;
    const quantityHeader = this.quantityHeader() ?? this.defaultQuantityHeader;
    const sections = [
      { header: binaryHeader, options: filtered.filter((o) => o.option.quantityMax <= 1) },
      { header: quantityHeader, options: filtered.filter((o) => o.option.quantityMax > 1) },
    ];
    return this.clearHeadersIfSingleCategory(sections);
  });

  private readonly selectedItems = computed(() => {
    const options = this.options();
    const quantities = this.selectedQuantities();

    return options
      .map((option) => {
        const quantity = quantities.get(this.optionKey(option)) ?? 0;
        if (quantity <= 0) return undefined;
        return MonitoringProductItem.create(option, quantity);
      })
      .filter((item): item is MonitoringProductItem => !!item);
  });

  constructor() {
    effect(() => {
      const options = this.options();
      if (!options?.length) return;

      const currentMap = untracked(() => this.selectedQuantities());
      const next = new Map(currentMap);
      const newKeys = new Set(options.map((o) => this.optionKey(o)));

      for (const option of options) {
        const key = this.optionKey(option);
        if (option.quantityMin > 0 && !next.has(key)) {
          next.set(key, option.quantityMin);
        }
      }

      for (const key of next.keys()) {
        if (!newKeys.has(key)) next.delete(key);
      }

      this.selectedQuantities.set(next);
    });

    effect(() => {
      const preselected = this.preselectedOptions();
      if (!preselected?.length || this.preselectedApplied) return;

      this.preselectedApplied = true;
      const currentMap = untracked(() => this.selectedQuantities());
      const next = new Map(currentMap);
      for (const item of preselected) {
        next.set(this.optionKey(item.option), item.quantity);
      }

      this.selectedQuantities.set(next);
    });

    toObservable(this.selectedItems)
      .pipe(takeUntilDestroyed())
      .subscribe((items) => this.selectionChange.emit(items));
  }

  protected search(value: string): void {
    this.searchValue.set(value);
  }

  protected getSelectedQuantity(option: FamilyStepOption): number {
    return this.selectedQuantities().get(this.optionKey(option)) ?? 0;
  }

  protected toggleOption(checked: boolean, option: FamilyStepOption): void {
    const quantity = checked ? Math.max(option.quantityMin, 1) : 0;
    this.updateSelectedQuantity(quantity, option);
  }

  protected updateSelectedQuantity(quantity: number, option: FamilyStepOption): void {
    const key = this.optionKey(option);
    const next = new Map(this.selectedQuantities());

    if (quantity <= 0) {
      next.delete(key);
    } else {
      next.set(key, quantity);
    }

    this.selectedQuantities.set(next);
  }

  protected trackOption(option: OptionDisplay): string {
    return this.optionKey(option.option);
  }

  private clearHeadersIfSingleCategory(sections: OptionSection[]): OptionSection[] {
    const nonEmpty = sections.filter((s) => s.options.length > 0);
    if (nonEmpty.length <= 1) {
      return nonEmpty.map((s) => ({ ...s, header: "" }));
    }
    return sections;
  }

  private sortOptions(options: OptionDisplay[]): OptionDisplay[] {
    return [...options].sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
  }

  private optionKey(option: FamilyStepOption): string {
    return getConfigurationOptionIdentifier(this.familyName(), option);
  }

  protected readonly StepSelectionType = StepSelectionType;
}
